import { makeStyles } from '@material-ui/core/styles';

// Design tokens aligned with the React Admin dark theme used across the app
// (see web/src/components/layout/theme.js and web/src/style.js).
// Accent = #3CE7EA (cyan) matches every other primary action in the app.
const tokens = {
  bgDark: '#303030',
  bgCard: '#424242',
  bgInput: '#5A5A5A',
  border: '#616161',
  accent: '#3CE7EA',
  accentHover: '#3CE7EA',
  accentDim: 'rgba(60,231,234,0.15)',
  accentText: '#2A2A2A', // text color on top of accent fills
  text: '#FFFFFF',
  textDim: '#AAAAAA',
  textLabel: '#CECECE',
  danger: '#ff5c6c',
  warning: '#ffb340',
  info: '#4da6ff',
  purple: '#9b6dff',
};

export { tokens };

export const useBrsCreateStyles = makeStyles(() => ({
  root: {
    width: '100%',
    marginTop: 24,
    position: 'relative',
  },
  breadcrumbLink: {
    color: tokens.accent,
    fontSize: 16,
    fontFamily: 'sans-serif, Roboto',
    fontWeight: 400,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  content: {
    width: '100%',
    padding: '24px 24px',
    boxSizing: 'border-box',
  },

  // Section header (e.g. "New Request", "Destinations")
  sectionHeader: {
    fontSize: 15,
    fontWeight: 600,
    color: tokens.text,
    padding: '12px 16px',
    background: tokens.bgCard,
    border: `1px solid ${tokens.border}`,
    borderBottom: 'none',
  },

  // Card (below section header)
  card: {
    background: tokens.bgCard,
    border: `1px solid ${tokens.border}`,
    borderTop: 'none',
    padding: 20,
    marginBottom: 16,
  },
  cardStandalone: {
    background: tokens.bgCard,
    border: `1px solid ${tokens.border}`,
    padding: 20,
    marginBottom: 16,
  },

  // ── Form elements ──────────────────────────────
  formRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  formGroup: {
    flex: 1,
    minWidth: 120,
  },
  formGroupW2: {
    flex: 2,
    minWidth: 150,
  },
  formGroupW3: {
    flex: 3,
    minWidth: 150,
  },
  formGroupFixedSm: {
    flex: '0 0 130px',
    minWidth: 130,
  },
  formGroupFixedMd: {
    flex: '0 0 180px',
    minWidth: 180,
  },
  formLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: tokens.textLabel,
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  formInput: {
    width: '100%',
    background: tokens.bgInput,
    border: 'none',
    borderBottom: `2px solid ${tokens.border}`,
    borderRadius: '4px 4px 0 0',
    padding: '9px 12px',
    color: tokens.text,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.15s, background 0.15s',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:hover': { background: '#6B6B6B' },
    '&:focus': { borderBottomColor: tokens.accent },
    '&::placeholder': { color: tokens.textDim },
  },
  formInputReadonly: {
    opacity: 0.55,
    cursor: 'not-allowed',
    background: tokens.bgInput,
    '&:hover': { background: tokens.bgInput },
  },

  // ── Flight-window date pickers (react-dates SingleDatePicker) ──────────
  // Restyles the react-dates input to match the dark-theme `formInput` look
  // used everywhere else on this form. The calendar popup keeps the library's
  // default theme (matching the existing Strata DateRangePicker usage); blocked
  // weekdays render greyed/struck-through via react-dates' built-in styling.
  flightDatePicker: {
    '& .SingleDatePicker, & .SingleDatePickerInput': {
      display: 'block',
      width: '100%',
    },
    '& .SingleDatePickerInput': {
      background: tokens.bgInput,
      border: 'none',
      borderBottom: `2px solid ${tokens.border}`,
      borderRadius: '4px 4px 0 0',
      transition: 'border-color 0.15s, background 0.15s',
      '&:hover': { background: '#6B6B6B' },
    },
    '& .DateInput': {
      width: '100%',
      background: 'transparent',
    },
    '& .DateInput_input': {
      width: '100%',
      background: 'transparent',
      border: 'none',
      color: tokens.text,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 'normal',
      padding: '9px 12px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      '&::placeholder': { color: tokens.textDim },
    },
    '& .DateInput_input__focused': {
      borderBottom: `2px solid ${tokens.accent}`,
    },
  },
  formSelect: {
    width: '100%',
    background: tokens.bgInput,
    border: 'none',
    borderBottom: `2px solid ${tokens.border}`,
    borderRadius: '4px 4px 0 0',
    padding: '9px 12px',
    color: tokens.text,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' fill='%23AAAAAA' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    paddingRight: 28,
    transition: 'border-color 0.15s, background 0.15s',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:hover': { background: '#6B6B6B' },
    '&:focus': { borderBottomColor: tokens.accent },
  },

  // ── Toggle switch ──────────────────────────────
  toggleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  toggle: {
    width: 40,
    height: 22,
    background: tokens.bgInput,
    borderRadius: 11,
    position: 'relative',
    cursor: 'pointer',
    transition: 'background 0.2s',
    border: `1px solid ${tokens.border}`,
    flexShrink: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      width: 16,
      height: 16,
      background: '#fff',
      borderRadius: '50%',
      top: 2,
      left: 2,
      transition: 'transform 0.2s',
    },
  },
  toggleOn: {
    background: `${tokens.accent} !important`,
    borderColor: `${tokens.accent} !important`,
    '&::after': {
      transform: 'translateX(18px)',
    },
  },
  toggleText: {
    fontSize: 12,
    color: tokens.textLabel,
    whiteSpace: 'nowrap',
  },

  // ── Buttons ────────────────────────────────────
  // Match the app-wide button convention from web/src/style.js:
  // square corners, no text-transform, opacity-based hover, cyan accent.
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 'unset',
    fontSize: 13,
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.15s, background 0.15s, color 0.15s, border-color 0.15s',
    fontFamily: 'inherit',
    textTransform: 'none',
  },
  btnPrimary: {
    background: tokens.accent,
    color: tokens.accentText,
    opacity: 0.9,
    '&:hover': { background: tokens.accent, color: tokens.accentText, opacity: 1 },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  btnGhost: {
    background: 'transparent',
    color: tokens.accent,
    border: `1px solid ${tokens.accent}`,
    opacity: 0.9,
    '&:hover:not(:disabled)': {
      background: tokens.accent,
      color: tokens.accentText,
      opacity: 1,
    },
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
      color: tokens.textDim,
      borderColor: tokens.border,
      background: 'transparent',
    },
  },
  btnBulk: {
    background: 'rgba(255,92,108,0.12)',
    color: tokens.danger,
    border: '1px solid rgba(255,92,108,0.3)',
    fontWeight: 600,
    '&:hover': { background: 'rgba(255,92,108,0.2)' },
  },
  btnDangerText: {
    background: 'none',
    border: 'none',
    color: tokens.danger,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    fontFamily: 'inherit',
  },
  btnAddText: {
    background: 'none',
    border: 'none',
    color: tokens.accent,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 0',
    fontFamily: 'inherit',
    '&:hover': { textDecoration: 'underline' },
  },

  // ── Badges ─────────────────────────────────────
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  badgeExisting: {
    background: 'rgba(155,109,255,0.15)',
    color: tokens.purple,
  },
  badgeNew: {
    background: 'rgba(77,166,255,0.15)',
    color: tokens.info,
  },

  // ── ISCI Entry card ────────────────────────────
  isciEntry: {
    background: tokens.bgCard,
    border: `1px solid ${tokens.border}`,
    marginBottom: 12,
    overflow: 'hidden',
  },
  isciEntryNew: {
    borderLeft: `3px solid ${tokens.info}`,
  },
  isciEntryExisting: {
    borderLeft: `3px solid ${tokens.purple}`,
  },
  isciTopRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: 12,
    borderBottom: `1px solid ${tokens.border}`,
  },
  isciNumber: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: tokens.bgInput,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: tokens.textDim,
    flexShrink: 0,
  },
  isciArrows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    flexShrink: 0,
  },
  isciArrowBtn: {
    background: 'none',
    border: 'none',
    color: tokens.textDim,
    cursor: 'pointer',
    fontSize: 10,
    padding: 0,
    lineHeight: 1,
    fontFamily: 'inherit',
    '&:hover': { color: tokens.text },
    '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
  },
  isciDateFields: {
    display: 'flex',
    gap: 10,
    flex: 1,
    alignItems: 'flex-end',
  },
  isciBody: {
    padding: 16,
  },

  // ── Original ISCI row ──────────────────────────
  origIsciRow: {
    display: 'flex',
    gap: 10,
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.15)',
    marginTop: 8,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  // Explicit field classes for inside orig-isci-row
  origIsciField: {
    flex: 1,
    minWidth: 100,
  },
  origIsciFieldFixed: {
    flex: '0 0 180px',
    minWidth: 180,
  },
  origIsciFieldBrandCode: {
    flex: '0 0 130px',
    minWidth: 130,
  },

  // ── Total duration ─────────────────────────────
  totalDuration: {
    padding: '8px 16px',
    fontSize: 12,
    fontWeight: 600,
    color: tokens.textDim,
    borderTop: `1px solid ${tokens.border}`,
  },

  // ── Split week toggle (collapsible) ────────────
  splitWeekToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    background: 'rgba(155,109,255,0.06)',
    borderTop: `1px solid ${tokens.border}`,
    cursor: 'pointer',
    transition: 'background 0.15s',
    userSelect: 'none',
    '&:hover': { background: 'rgba(155,109,255,0.1)' },
  },
  splitWeekArrow: {
    fontSize: 12,
    color: tokens.purple,
    transition: 'transform 0.2s',
    display: 'inline-block',
  },
  splitWeekArrowOpen: {
    transform: 'rotate(90deg)',
  },
  splitWeekLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: tokens.purple,
  },
  splitWeekHint: {
    fontSize: 11,
    color: tokens.textDim,
    marginLeft: 'auto',
  },
  splitWeekPanel: {
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.35s ease',
    background: 'rgba(0,0,0,0.1)',
  },
  splitWeekPanelOpen: {
    maxHeight: 600,
  },
  splitWeekInner: {
    padding: 16,
  },

  // ── Timeline grid (split week schedule) ────────
  timelineGrid: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 600,
      color: tokens.textDim,
      padding: '6px 4px',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${tokens.border}`,
    },
    '& th:first-child': {
      textAlign: 'left',
      width: 180,
    },
    '& td': {
      padding: 4,
    },
    '& td:first-child': {
      fontSize: 12,
      color: tokens.text,
      width: 180,
      verticalAlign: 'middle',
    },
  },
  isciMono: {
    fontFamily: "'SF Mono','Fira Code',monospace",
    fontSize: 11,
    color: tokens.accent,
  },
  spotName: {
    fontSize: 10,
    color: tokens.textDim,
    display: 'block',
  },
  dayCell: {
    height: 34,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
    userSelect: 'none',
    minWidth: 46,
  },
  dayCellPct100: { background: tokens.accent, color: tokens.accentText },
  dayCellPct50: { background: 'rgba(60,231,234,0.45)', color: tokens.accentText },
  dayCellPct25: { background: 'rgba(60,231,234,0.22)', color: tokens.accent },
  dayCellPct0: { background: tokens.bgInput, color: tokens.textDim },
  // Editable cell input inside .dayCell — strips browser chrome so the
  // cell still looks like a colored block, not a form field.
  dayCellInput: {
    width: '100%',
    height: '100%',
    background: 'transparent',
    border: 'none',
    textAlign: 'center',
    color: 'inherit',
    font: 'inherit',
    fontWeight: 700,
    outline: 'none',
    padding: 0,
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '&:focus': { outline: `1px solid ${tokens.accent}` },
  },
  cycleHint: {
    fontSize: 11,
    color: tokens.textDim,
    marginTop: 10,
    padding: '8px 12px',
    background: 'rgba(77,166,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  // ── Weekly Rotation Summary ────────────────────
  weeklyRotationTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    minWidth: 700,
    '& thead th': {
      textAlign: 'center',
      padding: '10px 8px',
      color: tokens.textDim,
      fontWeight: 500,
      fontSize: 12,
      borderBottom: `1px solid ${tokens.border}`,
    },
    '& thead th:first-child': {
      textAlign: 'left',
      width: '22%',
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    '& tbody td': {
      textAlign: 'center',
      padding: '8px 4px',
    },
    '& tbody td:first-child': {
      textAlign: 'left',
      padding: 12,
      verticalAlign: 'middle',
    },
    '& tbody tr': {
      borderBottom: `1px solid ${tokens.border}`,
    },
  },
  rotationCell: {
    display: 'inline-block',
    padding: '5px 12px',
    fontWeight: 600,
    fontSize: 12,
    minWidth: 56,
  },
  rotationCellActive: {
    background: tokens.accent,
    color: tokens.accentText,
  },
  rotationCellHalf: {
    background: 'rgba(60,231,234,0.35)',
    color: tokens.accent,
  },
  rotationCellEmpty: {
    background: tokens.bgInput,
    color: tokens.textDim,
  },
  // Editable input inside .rotationCell — keeps the cell pill look while
  // letting operators type arbitrary integers per spot/day.
  rotationCellInput: {
    width: 44,
    background: 'transparent',
    border: 'none',
    textAlign: 'center',
    color: 'inherit',
    font: 'inherit',
    fontWeight: 600,
    outline: 'none',
    padding: 0,
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '&:focus': { outline: `1px solid ${tokens.accent}` },
  },
  rotationCellTotal: {
    background: tokens.accentDim,
    color: tokens.accent,
    fontWeight: 700,
  },
  rotationSpotIsci: {
    color: tokens.accent,
    fontFamily: "'SF Mono',monospace",
    fontSize: 12,
    fontWeight: 600,
  },
  rotationSpotName: {
    color: tokens.textDim,
    fontSize: 11,
    marginTop: 2,
  },

  // ── Broadcast header ───────────────────────────
  broadcastHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    marginBottom: 8,
  },
  broadcastTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: tokens.text,
  },
  broadcastActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },

  // ── Hybrid mode banner ─────────────────────────
  hybridBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    fontSize: 12,
    marginBottom: 12,
    background: 'rgba(155,109,255,0.08)',
    border: '1px solid rgba(155,109,255,0.25)',
    color: tokens.purple,
  },

  // ── SD auto-enable banner ──────────────────────
  sdBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    fontSize: 12,
    marginTop: 10,
    background: 'rgba(255,179,64,0.08)',
    border: '1px solid rgba(255,179,64,0.2)',
    color: tokens.warning,
  },

  // ── Destinations ───────────────────────────────
  destSearch: {
    width: '100%',
    background: tokens.bgInput,
    border: 'none',
    borderBottom: `2px solid ${tokens.border}`,
    borderRadius: '4px 4px 0 0',
    padding: '9px 12px',
    color: tokens.text,
    fontSize: 14,
    outline: 'none',
    marginBottom: 10,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': { background: '#6B6B6B' },
    '&:focus': { borderBottomColor: tokens.accent },
    '&::placeholder': { color: tokens.textDim },
  },
  destChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  destChip: {
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    border: `1px solid ${tokens.border}`,
    background: tokens.bgInput,
    color: tokens.textDim,
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    '&:hover': { borderColor: tokens.accent, color: tokens.accent },
  },
  destChipSelected: {
    background: tokens.accentDim,
    borderColor: `${tokens.accent} !important`,
    color: `${tokens.accent} !important`,
  },
  destChipClose: {
    fontSize: 14,
    opacity: 0.7,
    cursor: 'pointer',
    '&:hover': { opacity: 1 },
  },
  destIdsLabel: {
    fontSize: 11,
    color: tokens.textLabel,
    marginBottom: 6,
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  destIds: {
    background: tokens.bgInput,
    border: `1px solid ${tokens.border}`,
    padding: '10px 14px',
    minHeight: 60,
  },
  // Autocomplete popover under the search input (the only place the full
  // destination catalog is visible).
  destAutocompleteList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 20,
    background: tokens.bgCard,
    border: `1px solid ${tokens.border}`,
    maxHeight: 260,
    overflowY: 'auto',
    boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
    marginTop: -8,
  },
  destAutocompleteItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: 13,
    color: tokens.text,
    borderBottom: `1px solid ${tokens.border}`,
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: tokens.accentDim, color: tokens.accent },
  },
  destAutocompleteCall: {
    fontFamily: "'SF Mono','Fira Code',monospace",
    fontWeight: 600,
    color: tokens.accent,
    minWidth: 80,
  },
  destAutocompleteName: {
    color: tokens.textDim,
    fontSize: 12,
  },
  destAutocompleteEmpty: {
    padding: '12px 14px',
    color: tokens.textDim,
    fontSize: 12,
  },

  // ── Add ISCI button ────────────────────────────
  addIsciButton: {
    textAlign: 'center',
    padding: '12px 0',
  },
  addIsciFullWidth: {
    width: '100%',
    justifyContent: 'center',
  },
  addIsciHint: {
    fontSize: 11,
    color: tokens.textDim,
    marginTop: 6,
  },

  // ── Footer ─────────────────────────────────────
  formFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 0',
    borderTop: `1px solid ${tokens.border}`,
    marginTop: 20,
  },
  helpText: {
    fontSize: 12,
    color: tokens.textDim,
  },
  footerActions: {
    display: 'flex',
    gap: 10,
  },

  // ── Validation / helper text ───────────────────
  fieldError: {
    fontSize: 11,
    color: tokens.danger,
    marginTop: 4,
  },
  helperText: {
    fontSize: 11,
    color: tokens.textDim,
    marginTop: 4,
  },

  // ── Loading overlay ────────────────────────────
  loadingOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: tokens.accent,
  },

  // ── Phase placeholder banner ───────────────────
  phasePlaceholder: {
    padding: '24px 16px',
    textAlign: 'center',
    color: tokens.textDim,
    fontSize: 13,
  },
  phasePlaceholderIcon: {
    fontSize: 28,
    marginBottom: 8,
    display: 'block',
  },
  phasePlaceholderLabel: {
    color: tokens.purple,
    fontWeight: 600,
  },
}));
